import { useQueries } from '@tanstack/react-query';
import {
  homeSectionProductsQueryOptions,
  isAutoSection,
  useHomeSectionProductMutations,
  useHomeSectionsQuery,
} from '@/features/cms/mainPage/hooks/useHomeSectionsQuery';
import type { HomeSectionProduct } from '@/features/cms/mainPage/types/homeSection';

/** 상품 폼에 보여 줄 메인 섹션 한 줄. */
export interface ProductHomeSectionOption {
  sectionKey: string;
  label: string;
  /** 자동 진열 섹션 — 매핑은 저장되지만 쇼핑몰 메인은 최신 등록순이라 안내를 붙인다. */
  auto: boolean;
}

/** sectionKey → 이 상품이 그 섹션에 있는가. */
export type ProductHomeSections = Record<string, boolean>;

export interface ProductHomeSectionsState {
  /** 섹션·섹션 상품 목록을 아직 읽는 중. */
  isPending: boolean;
  /** 상태를 아는(목록을 읽은) 사용 중 섹션만, 메인 노출 순서대로. */
  options: ProductHomeSectionOption[];
  /**
   * 섹션별 포함 여부. 읽은 섹션이 하나도 없으면 undefined — 화면은 체크를 숨기고 저장 때 섹션을 건드리지 않는다.
   * 목록을 못 읽은 섹션은 키가 없다(모르는 상태로 빼 버리지 않게).
   */
  included: ProductHomeSections | undefined;
  /** 저장 후 호출 — 섹션마다 want 와 지금 상태가 다를 때만 추가(맨 앞)·삭제한다. 실패한 섹션 라벨을 돌려준다. */
  sync: (productId: string, want: ProductHomeSections) => Promise<string[]>;
}

/**
 * QA-463/570 — 상품 등록·수정의 "메인 페이지 섹션 노출". QA-463 의 신상품(NEW RELEASES) 체크를 사용 중인 모든 메인 섹션
 * (EVER MUSIC·EVER MD 등)으로 넓혔다. 메인 페이지 관리 > 섹션 상품 매핑과 같은 홈 섹션 API(BE 변경 없음).
 * 추가는 섹션 맨 앞(최신 우선). 순서 조정은 메인 페이지 관리 화면.
 */
export const useProductHomeSections = (productId: string | null): ProductHomeSectionsState => {
  const sectionsQuery = useHomeSectionsQuery();
  const sections = [...(sectionsQuery.data ?? [])]
    .filter((s) => s.isActive)
    .sort((a, b) => a.sortOrder - b.sortOrder);
  const productQueries = useQueries({
    queries: sections.map((s) => homeSectionProductsQueryOptions(s.id)),
  });
  const { add, reorder, remove } = useHomeSectionProductMutations();

  const isPending = sectionsQuery.isPending || productQueries.some((q) => q.isPending);
  const known = sections
    .map((section, i) => ({ section, products: productQueries[i]?.data as HomeSectionProduct[] | undefined }))
    .filter((k): k is { section: (typeof sections)[number]; products: HomeSectionProduct[] } => k.products !== undefined);

  const options: ProductHomeSectionOption[] = known.map(({ section }) => ({
    sectionKey: section.sectionKey,
    label: section.label,
    auto: isAutoSection(section),
  }));
  const included: ProductHomeSections | undefined =
    known.length === 0
      ? undefined
      : Object.fromEntries(
          known.map(({ section, products }) => [
            section.sectionKey,
            productId ? products.some((p) => p.productId === productId) : false,
          ]),
        );

  const sync = async (id: string, want: ProductHomeSections): Promise<string[]> => {
    const failed: string[] = [];
    for (const { section, products } of known) {
      const w = want[section.sectionKey];
      if (w === undefined) continue;
      const has = products.some((p) => p.productId === id);
      try {
        if (w && !has) {
          await add.mutateAsync({ sectionId: section.id, productIds: [id] });
          const rest = [...products].sort((a, b) => a.sortOrder - b.sortOrder).map((p) => p.productId);
          await reorder.mutateAsync({ sectionId: section.id, productIds: [id, ...rest] });
        } else if (!w && has) {
          await remove.mutateAsync({ sectionId: section.id, productId: id });
        }
      } catch {
        failed.push(section.label);
      }
    }
    return failed;
  };

  return { isPending, options, included, sync };
};
